import { ReactNode, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { TabValue } from '@fluentui/react-components';

import { TMap } from '@shared/types';
import { layoutTransition } from '@shared/constants/layoutTransition.constants';
import { useDocumentTitle, useSearchParams } from '@shared/hooks';
import { SingleFieldDialog, TSingleFieldDialogState } from '@shared/components';
import { LayoutContext } from '@shared/contexts';

import { TFile, TFileVersion } from '@modules/files';
import { TReplica } from '@modules/replicas';
import { ApprovalsTab, TViewApprovalDialogState } from '@modules/approvals';
import { AllComments, useCommentStore } from '@modules/comments';
import { MockAiFindingsSidebar } from '@modules/mocks';
import {
  AiAssistant,
  EAiAssistantOriginItemType,
  useClearGroundedQaScrollParams,
  useCanUseAiAssistantSidebar,
  useRegisterAiAssistantOrigin,
} from '@modules/ai';
import { isTDocument, TDocument, TDraft, TVersion, getDocumentConfig } from '@modules/documents';
import getAiDisabledDocumentReason from '@modules/documents/helpers/getAiDisabledDocumentReason.helper';
import { PostChangeImpactAnalysis, PostImpactSummary, PreChangeImpactAnalysis } from '@modules/impacts';

import splitExtension from '../helpers/splitExtension.helper';
import { useRename } from '../api/items.api';
import { isTDraft, TResolvedItemsMap } from '../types/items.types';
import { TPermissions } from '@modules/access/types';
import { useCanEdit } from '@modules/access/hooks/useCanEdit.hook';
import useItemPageLayout from '../hooks/useItemPageLayout.hook';
import useItemScreenStore, { EItemScreenMode } from '../stores/useItemScreenStore';
import ItemsNavDrawer from './nav/ItemsNavDrawer';
import SidebarEnterTransition from './SidebarEnterTransition';
import FileIcon from './FileIcon';
import HistoryTab from './tabs/HistoryTab';
import { SCREEN_MODE_TO_PARAM_MAP } from '../constants';

type TProps<T extends TDocument | TFile> = {
  item: T;
  permissions: TPermissions;
  resolvedItems: TResolvedItemsMap;
  version?: TVersion | TFileVersion | TDraft;
  replica?: TReplica;
  navTabs: TMap<string, ReactNode>;
  renderHeader: (selectedTab: TabValue, setSelectedTab: (value: TabValue) => void) => ReactNode;
  content: ReactNode;
  setViewApprovalDialogState: (state?: TViewApprovalDialogState) => void;
  /** Whether the document has landscape sections (for auto-resize) */
  hasLandscapeSections?: boolean;
};

const ItemPage = <T extends TDocument | TFile>({
  item,
  permissions,
  resolvedItems,
  version,
  replica,
  navTabs,
  renderHeader,
  content,
  setViewApprovalDialogState,
  hasLandscapeSections,
}: TProps<T>) => {
  const mainItem = replica || item;
  const documentMainItem = isTDocument(mainItem) ? mainItem : undefined;
  const isDocumentAiDisabled = isTDocument(item) && Boolean(getAiDisabledDocumentReason(item));

  useRegisterAiAssistantOrigin(
    isTDocument(item) && !isDocumentAiDisabled
      ? { item: item._id, itemType: EAiAssistantOriginItemType.DOCUMENT }
      : undefined
  );

  const [selectedTab, setSelectedTab] = useState<TabValue>('home');
  const [simpleDialogState, setSimpleDialogState] = useState<TSingleFieldDialogState<'input'> | undefined>(undefined);

  // This controls the right drawer:
  const { screenMode, setScreenMode } = useItemScreenStore();
  const { setSelectedCommentId, selectedCommentId } = useCommentStore();
  const clearGroundedQaScrollParams = useClearGroundedQaScrollParams();
  const canUseAiAssistantSidebar = useCanUseAiAssistantSidebar();
  const { canEdit } = useCanEdit(permissions.accessRight);
  const isAiAssistantAvailable = isTDocument(item) && canUseAiAssistantSidebar && !isDocumentAiDisabled;

  // Handle rename, used in the layout
  const rename = useRename();
  const onRename = () => {
    const res = splitExtension(mainItem.name);
    let defaultText = mainItem.name;
    let afterLabel = '';
    if (res[1]) {
      defaultText = res[0];
      afterLabel = `.${res[1]}`;
    }
    const dialogState: TSingleFieldDialogState<'input'> = {
      isOpen: true,
      defaultText,
      title: 'Rename',
      label: 'Name',
      buttonLabel: 'Rename',
      afterLabel,
      onSubmit: (data) => {
        rename.mutate(
          { id: mainItem._id, itemType: mainItem.type, name: data.text + afterLabel },
          { onSuccess: () => setSimpleDialogState({ ...dialogState, isOpen: false }) }
        );
      },
    };
    setSimpleDialogState(dialogState);
  };

  useDocumentTitle(mainItem.name);

  // Setup layout with some metadata about the item
  const { setLayoutState } = useContext(LayoutContext);
  useEffect(() => {
    const layoutIcon = (
      <FileIcon
        type={mainItem.type}
        path={item.resolvedPath.map((pathItem) => pathItem._id)}
        isBlock={Boolean(documentMainItem?.isBlock)}
        isFrozen={item.isFrozen}
        isTemplate={Boolean(documentMainItem?.isTemplate)}
        mimetype={(version as TFileVersion)?.mimetype}
        size="small"
        name={item.name}
      />
    );

    setLayoutState({
      _id: mainItem._id,
      name: mainItem.name,
      icon: layoutIcon,
      resolvedPath: mainItem.resolvedPath,
      onRename,
    });
  }, [
    mainItem._id,
    mainItem.name,
    mainItem.resolvedPath,
    mainItem.type,
    documentMainItem?.isBlock,
    documentMainItem?.isTemplate,
    (version as TFileVersion)?.mimetype,
    item.name,
  ]);

  // Parse the URL params
  const { searchParams } = useSearchParams();
  const commentParamId = searchParams.get('comment');
  const screenModeParamId = searchParams.get('screenMode');

  useEffect(() => {
    /**
     * If there's a ?comment=someId in the URL and the selected comment is not set:
     * - open the comments sidebar
     * - select an initial comment
     */
    if (commentParamId && !selectedCommentId) {
      // Open the comments sidebar
      setScreenMode(EItemScreenMode.COMMENTS);
      // Select an initial comment
      setSelectedCommentId(commentParamId);
    }

    /**
     * If there's a ?screenMode=someId in the URL and the screen mode is not set:
     * - find the `EItemScreenMode` value based on the URL param
     * - set the screen mode
     */
    if (screenModeParamId && !screenMode) {
      // Find the `EItemScreenMode` value based on the URL param
      const newMode = Object.keys(SCREEN_MODE_TO_PARAM_MAP).find(
        (key) => SCREEN_MODE_TO_PARAM_MAP[key as EItemScreenMode] === screenModeParamId
      );
      if (newMode) {
        if (newMode === EItemScreenMode.AI && !isAiAssistantAvailable) return;
        setScreenMode(newMode as EItemScreenMode);
      } else console.error(`Unknown screen mode: ${screenModeParamId}`);
    }
  }, []);

  useEffect(() => {
    if (screenMode === EItemScreenMode.AI && !isAiAssistantAvailable) {
      clearGroundedQaScrollParams();
      setScreenMode();
    }
  }, [clearGroundedQaScrollParams, isAiAssistantAvailable, screenMode, setScreenMode]);

  // Switch between post-change and summary impact analysis
  const documentVersion = version as TVersion | undefined;
  useEffect(() => {
    if (documentVersion?.impactAnalysis && screenMode === EItemScreenMode.POST_CHANGE_IMPACT_ANALYSIS) {
      setScreenMode(EItemScreenMode.IMPACT_ANALYSIS_SUMMARY);
    } else if (screenMode === EItemScreenMode.IMPACT_ANALYSIS_SUMMARY && !documentVersion?.impactAnalysis) {
      setScreenMode(EItemScreenMode.POST_CHANGE_IMPACT_ANALYSIS);
    }
  }, [documentVersion, screenMode]);

  const isSpreadsheet = isTDocument(item) && (item as TDocument).mock?.isSpreadsheet;
  const isLandscape =
    isTDocument(item) && version && getDocumentConfig((version as TVersion | TDraft).config).landscape;

  // Container needs landscape width when: document is landscape OR has landscape sections
  const effectiveLandscape = isLandscape || hasLandscapeSections;

  const { maxWidth, paddingRight, isAiSidebar, sidebarWidthPx, isOpenDrawer, setIsOpenDrawer } = useItemPageLayout({
    screenMode,
    hasVersion: Boolean(version),
    isDocument: isTDocument(item),
    isAiAssistantAvailable,
    isSpreadsheet: Boolean(isSpreadsheet),
    effectiveLandscape: Boolean(effectiveLandscape),
  });

  const sidebar = !version ? null : screenMode === EItemScreenMode.COMMENTS ? (
    <AllComments item={item} permissions={permissions} version={version} resolvedItems={resolvedItems} />
  ) : screenMode === EItemScreenMode.MOCK_AI_FINDINGS && isTDocument(item) ? (
    <MockAiFindingsSidebar documentName={item.name} />
  ) : screenMode === EItemScreenMode.AI && isAiAssistantAvailable ? (
    <AiAssistant
      surface="sidebar"
      origin={isTDocument(item) ? { item: item._id, itemType: EAiAssistantOriginItemType.DOCUMENT } : undefined}
      canEditDocument={isTDocument(item) && canEdit}
      onClose={() => {
        clearGroundedQaScrollParams();
        setScreenMode();
      }}
    />
  ) : screenMode === EItemScreenMode.PRE_CHANGE_IMPACT_ANALYSIS ? (
    <PreChangeImpactAnalysis item={item} resolvedItems={resolvedItems} version={version} />
  ) : screenMode === EItemScreenMode.POST_CHANGE_IMPACT_ANALYSIS && !isTDraft(version) ? (
    <PostChangeImpactAnalysis item={item} resolvedItems={resolvedItems} version={version} />
  ) : screenMode === EItemScreenMode.IMPACT_ANALYSIS_SUMMARY && !isTDraft(version) ? (
    <PostImpactSummary item={item} resolvedItems={resolvedItems} version={version} permissions={permissions} />
  ) : null;

  return (
    <div className="flex flex-1" style={{ paddingRight, transition: layoutTransition('padding-right') }}>
      <SingleFieldDialog state={simpleDialogState} setState={setSimpleDialogState} />
      <ItemsNavDrawer
        isOpen={isOpenDrawer}
        setIsOpen={setIsOpenDrawer}
        tabs={navTabs}
        resolvedPath={mainItem.resolvedPath}
      />
      <div
        className={classNames('relative grow h-full px-4', {
          'mx-auto pl-3': !isSpreadsheet,
        })}
        style={{
          maxWidth,
          transition: layoutTransition('max-width'),
        }}
      >
        <div className="h-full flex flex-col">
          {renderHeader(selectedTab, setSelectedTab)}
          <div className="w-full h-full relative">
            {selectedTab === 'history' ? (
              <HistoryTab
                item={item}
                permissions={permissions}
                setSelectedTab={setSelectedTab}
                setViewApprovalDialogState={setViewApprovalDialogState}
                replica={replica}
              />
            ) : selectedTab === 'approvals' ? (
              <ApprovalsTab
                item={item}
                permissions={permissions}
                setViewApprovalDialogState={setViewApprovalDialogState}
              />
            ) : (
              <div
                className={classNames(
                  'flex flex-1 h-full mx-auto gap-4',
                  { 'pl-1 w-full': !isTDocument(item) },
                  { 'w-fit': !isSpreadsheet },
                  { 'max-w-[calc(100%+1rem)]': isAiSidebar } // 1rem keeps gap-4 within the existing width budget.
                )}
              >
                {content}
                {sidebar && <SidebarEnterTransition widthPx={sidebarWidthPx}>{sidebar}</SidebarEnterTransition>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemPage;
